import { memo } from "react";
import { Link } from "wouter";
import { useTranslation } from "react-i18next";
import { Home, Settings, LogOut, User, HelpCircle, FileText, Mail } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/hooks/use-auth";
import { getDisplayName, getInitials } from "@/lib/display-name";

/**
 * MobileUserMenu -- avatar dropdown for mobile header.
 * Provides access to Profile, Dashboard, Settings, and Sign Out
 * which are otherwise only available in the desktop sidebar.
 */
export const MobileUserMenu = memo(function MobileUserMenu() {
  const { user, logout } = useAuth();
  const { t } = useTranslation();

  if (!user) return null;

  const displayName = getDisplayName(user);
  const initials = getInitials(user);
  const profileUrl = `/profile/${user.id}`;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex items-center justify-center rounded-full min-h-[44px] min-w-[44px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold focus-visible:ring-offset-2"
          aria-label={t("mobileUserMenu.openMenu", "Open user menu")}
          data-testid="mobile-user-menu-trigger"
        >
          <Avatar className="h-8 w-8 border-2 border-brand-gold/35">
            {user.profileImageUrl && (
              <AvatarImage
                src={user.profileImageUrl}
                alt={displayName}
              />
            )}
            <AvatarFallback className="bg-primary text-primary-foreground text-xs font-medium">
              {initials}
            </AvatarFallback>
          </Avatar>
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        sideOffset={8}
        className="w-56 border-brand-gold/20 bg-card dark:bg-primary shadow-lg"
        data-testid="mobile-user-menu-content"
      >
        {/* User identity header */}
        <DropdownMenuLabel className="px-3 py-2">
          <p
            className="text-sm font-semibold text-foreground dark:text-primary-foreground truncate"
            data-testid="mobile-user-menu-name"
          >
            {displayName}
          </p>
          {user.email && (
            <p className="text-xs text-muted-foreground dark:text-brand-gold truncate">
              {user.email}
            </p>
          )}
        </DropdownMenuLabel>

        <DropdownMenuSeparator className="bg-brand-gold/20" />

        <DropdownMenuGroup>
          <DropdownMenuItem asChild>
            <Link
              href={profileUrl}
              className="flex items-center gap-2 cursor-pointer text-foreground dark:text-primary-foreground"
              data-testid="mobile-user-menu-profile"
            >
              <User className="h-4 w-4 text-brand-gold" />
              <span>{t("mobileUserMenu.profile", "Profile")}</span>
            </Link>
          </DropdownMenuItem>

          <DropdownMenuItem asChild>
            <Link
              href="/dashboard"
              className="flex items-center gap-2 cursor-pointer text-foreground dark:text-primary-foreground"
              data-testid="mobile-user-menu-dashboard"
            >
              <Home className="h-4 w-4 text-brand-gold" />
              <span>{t("mobileUserMenu.dashboard", "Dashboard")}</span>
            </Link>
          </DropdownMenuItem>

          <DropdownMenuItem asChild>
            <Link
              href="/settings"
              className="flex items-center gap-2 cursor-pointer text-foreground dark:text-primary-foreground"
              data-testid="mobile-user-menu-settings"
            >
              <Settings className="h-4 w-4 text-brand-gold" />
              <span>{t("mobileUserMenu.settings", "Settings")}</span>
            </Link>
          </DropdownMenuItem>
        </DropdownMenuGroup>

        <DropdownMenuSeparator className="bg-brand-gold/20" />

        {/* Help & Legal — otherwise unreachable on mobile (footer hidden by BottomNav) */}
        <DropdownMenuGroup>
          <DropdownMenuItem asChild>
            <Link
              href="/help-center"
              className="flex items-center gap-2 cursor-pointer text-foreground dark:text-primary-foreground"
              data-testid="mobile-user-menu-help"
            >
              <HelpCircle className="h-4 w-4 text-brand-gold" />
              <span>{t("mobileUserMenu.helpCenter", "Help Center")}</span>
            </Link>
          </DropdownMenuItem>

          <DropdownMenuItem asChild>
            <Link
              href="/contact"
              className="flex items-center gap-2 cursor-pointer text-foreground dark:text-primary-foreground"
              data-testid="mobile-user-menu-contact"
            >
              <Mail className="h-4 w-4 text-brand-gold" />
              <span>{t("mobileUserMenu.contact", "Contact Us")}</span>
            </Link>
          </DropdownMenuItem>

          <DropdownMenuItem asChild>
            <Link
              href="/terms-of-service"
              className="flex items-center gap-2 cursor-pointer text-foreground dark:text-primary-foreground"
              data-testid="mobile-user-menu-legal"
            >
              <FileText className="h-4 w-4 text-brand-gold" />
              <span>{t("mobileUserMenu.legal", "Legal")}</span>
            </Link>
          </DropdownMenuItem>
        </DropdownMenuGroup>

        <DropdownMenuSeparator className="bg-brand-gold/20" />

        <DropdownMenuItem
          onClick={() => logout()}
          className="flex items-center gap-2 cursor-pointer text-brand-coral focus:text-brand-coral focus:bg-brand-coral/10"
          data-testid="mobile-user-menu-signout"
        >
          <LogOut className="h-4 w-4" />
          <span>{t("mobileUserMenu.signOut", "Sign Out")}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
});
